import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { control } from 'react-validation';
import MaskedInput from 'react-text-mask';
import classnames from 'classnames';
import { createUseStyles } from '@glu/theming';
import AppPropTypes from '../../proptypes/AppPropTypes';
import styles from './Input.styles';

const useStyles = createUseStyles(styles);

// Define own Input component
export function InputComponent({
  bothDefined,
  error,
  id,
  isChanged,
  isUsed,
  label,
  locale,
  mask,
  maxLengthIndicatorLimit,
  name,
  nullable,
  onBlurValidation,
  optional,
  preventErrorMsg,
  showStatus,
  ...props
}) {
  const [focused, setFocused] = useState(false);
  const classes = useStyles();
  const showErrorDuringFocus = onBlurValidation ? !focused : true;
  const hasError = showErrorDuringFocus && isChanged && error;
  const valueLength = props.value ? props.value.toString().length : 0;

  const onBlur = (e) => {
    setFocused(false);
    if (props.onBlur) props.onBlur(e);
  };
  const onFocus = (e) => {
    setFocused(true);
    if (props.onFocus) props.onFocus(e);
  };

  const onBlurValidationProps = onBlurValidation ? { onFocus, onBlur } : {};

  return (
    <div className={classnames({ 'is-optional': optional, 'has-error has-feedback': hasError })}>
      {label && (
        <label htmlFor={id} className="control-label">
          {label}
        </label>
      )}
      {mask && (
        <MaskedInput id={id} name={name} mask={mask} {...props} {...onBlurValidationProps} />
      )}
      {!mask && <input id={id} name={name} {...props} {...onBlurValidationProps} />}
      <div className={classes.helpWrapper}>
        {!preventErrorMsg && hasError && hasError.length && (
          <small className="help-block">{hasError}</small>
        )}
        {maxLengthIndicatorLimit && (
          <small className={classnames(classes.lengthIndicator, { 'help-block': hasError })}>
            {valueLength}/{maxLengthIndicatorLimit}
          </small>
        )}
      </div>
    </div>
  );
}

InputComponent.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isChanged: PropTypes.bool,
  isUsed: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  mask: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]))
  ]),
  bothDefined: PropTypes.bool,
  nullable: PropTypes.bool,
  optional: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onBlurValidation: PropTypes.bool,
  showStatus: PropTypes.bool,
  locale: AppPropTypes.locale,
  preventErrorMsg: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxLengthIndicatorLimit: PropTypes.number
};
InputComponent.defaultProps = {
  optional: false,
  bothDefined: false,
  nullable: false,
  showStatus: false,
  label: null,
  error: '',
  onBlurValidation: false,
  onBlur: null,
  onFocus: null,
  isChanged: false,
  isUsed: false,
  id: '',
  name: '',
  mask: null,
  locale: {},
  preventErrorMsg: false,
  value: '',
  maxLengthIndicatorLimit: undefined
};

function areEqual(prev, next) {
  return (
    prev.value === next.value &&
    prev.error === next.error &&
    prev.disabled === next.disabled &&
    prev.isChanged === next.isChanged &&
    prev.isUsed === next.isUsed
  );
}

export const MemoizedInput = control(React.memo(InputComponent, areEqual));

export default control(InputComponent);
